import actionCreatorFactory from "../util/redux/action-factory";
import { reducerWithInitialState } from "../util/redux/typed-reducers";
import { ReduxDispatch, ReduxState } from "./reducers";
import { showSnackbar } from "./snackbar";
import * as request from "../util/request";
import { RootObject } from "../util/DTOs/NewsFeedDtos";
import { decodeHtmlEntities } from "../util/utils";
import logger from "util/logger";

/**
 * Action factory + actions
 */

export type NewsFeedDto = RootObject;
type AuthorImageDto = RootObject;

interface AuthorImage {
    itemId: number;
    authorImageUrl?: string;
}

const actionFactory = actionCreatorFactory("HypoMobiili/NewsFeed");
const loadNewsFeed = actionFactory<{}, NewsFeedDto[], string>("LOAD_NEWSFEED");
const loadAuthorImages = actionFactory<AuthorImage[], string>(
    "LOAD_AUTHOR_IMAGES"
);

const processResults = (objects: NewsFeedDto[]) => {
    let mappedObjects: NewsFeedDto[] = [];

    objects.forEach((object: NewsFeedDto) => {
        const mappedObject: NewsFeedDto = {
            ...object,
            title: {
                // Sometimes wordpress might return html entities in title, fix those
                rendered: decodeHtmlEntities(
                    (object.title && object.title.rendered) || ""
                ),
            },
            _links: {
                ...object._links,
                wpAttachment: object._links && object._links["wp:attachment"],
                wpFeaturedmedia:
                    object._links && object._links["wp:featuredmedia"],
                versionHistory:
                    object._links && object._links["version-history"],
                wpTerm: object._links && object._links["wp:term"],
            },
        };
        mappedObjects.push(mappedObject);
    });

    return mappedObjects;
};

export function loadNewsFeedItems() {
    return async (dispatch: ReduxDispatch) => {
        dispatch(loadNewsFeed({}));

        try {
            const result = await request.get<NewsFeedDto[]>(
                `https://www.hypo.fi/wp-json/wp/v2/posts`,
                true
            );
            const mappedResult = processResults(result);
            dispatch(loadNewsFeed.done(mappedResult));
            let authorImages = await Promise.all(
                mappedResult.map((item) => {
                    const url =
                        item._links &&
                        item._links.wpFeaturedmedia &&
                        item._links.wpFeaturedmedia[0].href;
                    if (url) {
                        return loadAuthorImage(item.id, url);
                    }
                    return { itemId: item.id, authorImageUrl: undefined };
                })
            );

            dispatch(
                loadAuthorImages(authorImages.filter(Boolean) as AuthorImage[])
            );
        } catch (error) {
            dispatch(showSnackbar("Uutisvirran lataaminen epäonnistui"));
            dispatch(loadNewsFeed.failed(error));
            throw error;
        }
    };
}

export async function loadAuthorImage(
    itemId: number,
    featuredMediaUrl: string
) {
    try {
        const result = await request.get<AuthorImageDto>(
            featuredMediaUrl,
            true
        );
        const authorImageUrl = result.guid && result.guid.rendered;
        return {
            itemId: itemId,
            authorImageUrl: authorImageUrl,
        };
    } catch (error) {
        logger.error(error);
    }
    return undefined;
}

/**
 * The internal state of this reducer
 */
export class NewsFeedState {
    items: NewsFeedDto[];
    loading: boolean;
}

/**
 * The reducer.
 *
 * The module exports this as default
 */

const initialNewsFeedState: NewsFeedState = {
    items: [],
    loading: false,
};

export const NewsFeedReducer = reducerWithInitialState(initialNewsFeedState)
    .case(loadNewsFeed, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(loadNewsFeed.done, (state, action) => {
        return {
            ...state,
            items: action.payload,
            loading: false,
        };
    })
    .case(loadNewsFeed.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    })
    .case(loadAuthorImages, (state, action) => {
        let items = state.items;
        items = items.map((item) => {
            const authorImage = action.payload.find(
                (i) => i.itemId === item.id
            );
            if (authorImage) {
                return {
                    ...item,
                    authorImageUrl: authorImage.authorImageUrl,
                };
            }
            return item;
        });

        return {
            ...state,
            items: items,
        };
    });

export default NewsFeedReducer;

/**
 * Selectors
 */
export const getNewsFeed = (state: ReduxState) => {
    return state.newsFeed;
};

export const getNewsFeedItem = (state: ReduxState, id: number) => {
    const items = state.newsFeed.items.filter(
        (i) => Number(i.id) === Number(id)
    );

    if (items.length === 1) {
        return items[0];
    }

    return undefined;
};

export const newsFeedLoading = (state: ReduxState) => {
    return state.newsFeed.loading;
};



// WEBPACK FOOTER //
// ./src/ducks/newsfeed.ts